// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  Divider,
  Collapse,
  CardMedia,
} from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  B3Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentSwitch,
  ModalAlertError,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Help Section Configuration
// ----------------------------------------------------------------------
const PENDING_ORDER_SETTINGS_HYSTERESIS_HELP_IMAGE = "/illustrations/converted/pending_order_settings/hysteresis.png";
const PENDING_ORDER_SETTINGS_HYSTERESIS_HELP_DESCRIPTION = (
  <>
    The hysteresis feature in the context of automated trading and trade abstraction extends the typically available pending order types. While most
    traders are familiar with market, limit, and stop orders, another official order type exists - the stop limit order. This order type requires the
    price first to increase, then decrease or decrease, and then increase again. However, this order type must be better integrated into MetaTrader 4
    or MetaTrader 5. Nevertheless, this order type is a convenient tool for automated trading. Instead of using the stop-limit order option, the
    Expert Advisor Builder introduces a feature called hysteresis. A hysteresis allows a wider range of exciting ways of handling pending orders.
    <br />
    <br />
    This feature is a software-only solution, available when local pending orders are enabled. Due to its technical nature, it is highly recommended
    that you familiarize yourself with each pending order combination possible with this feature. However, it's not necessary to use it at all. Most
    traders will likely be able to build profitable and long-term stable trading solutions without using this feature. The two input parameters below
    matter for those who wish to use it. The hysteresis feature is active only when the related on/off switched are enabled. By default, it is
    disabled.
    <br />
    <br />
    In simpler terms, <b>a pending order will only be executed when the price first surpasses the minimum value and then reaches the pending price</b>
    . However, <b>the order will be deleted if the maximum value is exceeded</b>. This allows for price-dependent deletion of pending orders and the
    implementation of stop-limit orders. This feature helps respond to anticipated future price changes, typically indicated by leading trading
    signals such as the RSI or the chart analyzer.
  </>
);

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const PendingOrderSettingsDefaults = {
  nMode: "0",
  nPrice: "0",
  nFixPipsActual: "300",
  dRateOfChangeActual: "0.00060000",
  nInosUsedForValueInjection: "0",
  nCaSupResUsedForValueInjection: "0",
  nLimits: "3",
  nReference: "0",
  nExpiration: "0",
  bFixPipsMin: false,
  nFixPipsMin: "4",
  bRateOfChangeMin: false,
  dRateOfChangeMin: "0.00000100",
  bFixPipsMax: false,
  nFixPipsMax: "400",
  bRateOfChangeMax: false,
  dRateOfChangeMax: "0.00001000",
  nLocalPending: true,
  bHysteresisMin: false,
  nHysteresisMin: "0",
  bHysteresisMax: false,
  nHysteresisMax: "0",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nFixPipsActual: {
    min: 5,
  },
  dRateOfChangeActual: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  nExpiration: {},
  nFixPipsMin: {
    min: 0,
  },
  dRateOfChangeMin: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  nFixPipsMax: {},
  dRateOfChangeMax: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  nHysteresisMin: {},
  nHysteresisMax: {},
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function PendingOrderSettingsRenderParameters(professionalMode, config, indicators, chartAnalyzers) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => config[key];

  // Helper function to get indicator name by ID
  const getIndicatorNameById = (indicatorId) => {
    if (!indicatorId || indicatorId === "0") return "None selected";
    if (!indicators || indicators.length === 0) return "No indicators available";

    const indicator = indicators.find((ind) => ind.id.toString() === indicatorId.toString());
    return indicator ? `${indicator.name} (${indicator.subtitle})` : `Unknown indicator (ID: ${indicatorId})`;
  };

  // Helper function to get chart analyzer name by ID
  const getChartAnalyzerNameById = (chartAnalyzerId) => {
    if (!chartAnalyzerId || chartAnalyzerId === "0") return "None selected";
    if (!chartAnalyzers || chartAnalyzers.length === 0) return "No chart analyzers available";

    const chartAnalyzer = chartAnalyzers.find((ca) => ca.id.toString() === chartAnalyzerId.toString());
    return chartAnalyzer ? `${chartAnalyzer.name} (${chartAnalyzer.subtitle})` : `Unknown chart analyzer (ID: ${chartAnalyzerId})`;
  };

  const getModeDisplay = () => {
    const value = config.nMode;
    switch (value) {
      case "0":
        return "Off";
      case "1":
        return "Stop";
      case "2":
        return "Limit";
      default:
        return "Not set";
    }
  };

  const getPriceDisplay = () => {
    const value = config.nPrice;
    switch (value) {
      case "0":
        return "Fix pips";
      case "1":
        return "Rate of change";
      case "2":
        return "Indicator value";
      case "3":
        return "Support/resistance from chart analyzer";
      default:
        return "Not set";
    }
  };

  const getLimitsDisplay = () => {
    const value = config.nLimits;
    switch (value) {
      case "0":
        return "Initial";
      case "1":
        return "TP moved";
      case "2":
        return "SL moved";
      case "3":
        return "SL and TP moved";
      case "4":
        return "TP from chart analyzer";
      case "5":
        return "SL from chart analyzer";
      case "6":
        return "SL and TP from chart analyzer";
      default:
        return "Not set";
    }
  };

  const getReferenceDisplay = () => {
    const value = config.nReference;
    switch (value) {
      case "0":
        return "Aks / bid";
      case "1":
        return "Ask / bid center";
      case "2":
        return "Median price";
      case "3":
        return "Typical price";
      case "4":
        return "Weighted price";
      default:
        return "Not set";
    }
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Mode", getModeDisplay())}

      {config.nMode !== "0" ? (
        <>
          {renderParam("Price calculation method", getPriceDisplay())}

          {config.nPrice === "0" ? renderParam("Fix pips", getDisplayValue("nFixPipsActual")) : null}

          {config.nPrice === "1" ? renderParam("Rate of change", getDisplayValue("dRateOfChangeActual")) : null}

          {config.nPrice === "2" ? renderParam("Used indicator", getIndicatorNameById(config.nInosUsedForValueInjection)) : null}

          {config.nPrice === "3" ? renderParam("Used chart analyzer", getChartAnalyzerNameById(config.nCaSupResUsedForValueInjection)) : null}

          {renderParam("Limits behaviour", getLimitsDisplay())}

          {renderParam("Used price reference", getReferenceDisplay())}

          {config.nExpiration === "0"
            ? renderParam("Expiration", "Not set")
            : renderParam("Expiration", config.nExpiration < 0 ? `${Math.abs(config.nExpiration)} seconds` : `${config.nExpiration} bars`)}

          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

          {config.bFixPipsMin === true
            ? renderParam("Minimum pending price in fix pips", getDisplayValue("nFixPipsMin"))
            : renderParam("Minimum pending price in fix pips", "Off")}

          {config.bRateOfChangeMin === true
            ? renderParam("Minimum pending price in rate of change", getDisplayValue("dRateOfChangeMin"))
            : renderParam("Minimum pending price in rate of change", "Off")}

          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

          {config.bFixPipsMax === true
            ? renderParam("Maximum pending price in fix pips", getDisplayValue("nFixPipsMax"))
            : renderParam("Maximum pending price in fix pips", "Off")}

          {config.bRateOfChangeMax === true
            ? renderParam("Maximum pending price in rate of change", getDisplayValue("dRateOfChangeMax"))
            : renderParam("Maximum pending price in rate of change", "Off")}

          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

          {renderParam("Locally managed limit and stop orders", config.nLocalPending ? "On" : "Off")}

          {config.nLocalPending === true ? (
            <>
              <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

              {config.bHysteresisMin === true
                ? renderParam("Hysteresis minimum value", getDisplayValue("nHysteresisMin"))
                : renderParam("Hysteresis minimum value", "Off")}

              {config.bHysteresisMax === true
                ? renderParam("Hysteresis maximum value", getDisplayValue("nHysteresisMax"))
                : renderParam("Hysteresis maximum value", "Off")}
            </>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const PendingOrderSettingsConfigure = forwardRef(
  ({ indicators, chartAnalyzers, onSave, initialData = null, professionalMode = null, nOperation = null }, ref) => {
    // ----------------------------------------------------------------------
    // Properties
    // ----------------------------------------------------------------------
    PendingOrderSettingsConfigure.propTypes = {
      indicators: PropTypes.any,
      chartAnalyzers: PropTypes.any,
      onSave: PropTypes.func.isRequired,
      initialData: PropTypes.object,
      professionalMode: PropTypes.any,
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const [scroll, setScroll] = useState("paper");
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [warning, setWarning] = useState(false);
    const [returnMessage, setReturnMessage] = useState("");

    const autoCloseTimeoutRef = useRef(null);

    const [collapseHysteresis, setCollapseHysteresis] = useState(false);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      openDialog: () => {
        setOpen(true);
      },
      closeDialog: () => {
        setOpen(false);
      },
    }));

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleReset = () => {
      // Clear all errors and touched state
      setErrors({});
      setTouched({});
      // Clear cross-validation error state
      setError(false);
      setReturnMessage("");

      // Apply all the default values to the form
      Object.keys(PendingOrderSettingsDefaults).forEach((key) => {
        if (key !== "nMode") {
          setFieldValue(key, PendingOrderSettingsDefaults[key]);
        }
      });
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleClose = () => {
      // Clear any existing auto-close timeout
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }

      setOpen(false);
      setError(false);
      setSuccess(false);
      setWarning(false);
      setReturnMessage("");
      resetForm();
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const formik = useFormik({
      initialValues: {
        //  parameters
        nMode: initialData?.nMode ?? PendingOrderSettingsDefaults.nMode,
        nPrice: initialData?.nPrice ?? PendingOrderSettingsDefaults.nPrice,
        nFixPipsActual: initialData?.nFixPipsActual ?? PendingOrderSettingsDefaults.nFixPipsActual,
        dRateOfChangeActual: initialData?.dRateOfChangeActual ?? PendingOrderSettingsDefaults.dRateOfChangeActual,
        // Updated logic for indicator selection
        nInosUsedForValueInjection: (() => {
          if (initialData?.nInosUsedForValueInjection) {
            return initialData.nInosUsedForValueInjection;
          }
          // Find first indicator with Signal used for = Forward (2)
          const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
          return forwardIndicators.length > 0 ? forwardIndicators[0].id.toString() : PendingOrderSettingsDefaults.nInosUsedForValueInjection;
        })(),
        // Updated logic for chart analyzer selection
        nCaSupResUsedForValueInjection: (() => {
          if (initialData?.nCaSupResUsedForValueInjection) {
            return initialData.nCaSupResUsedForValueInjection;
          }
          // Find first chart analyzer with Signal used for = Forward (2)
          const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
          return forwardChartAnalyzers.length > 0
            ? forwardChartAnalyzers[0].id.toString()
            : PendingOrderSettingsDefaults.nCaSupResUsedForValueInjection;
        })(),
        nLimits: initialData?.nLimits ?? PendingOrderSettingsDefaults.nLimits,
        nReference: initialData?.nReference ?? PendingOrderSettingsDefaults.nReference,
        nExpiration: initialData?.nExpiration ?? PendingOrderSettingsDefaults.nExpiration,
        bFixPipsMin: initialData?.bFixPipsMin ?? PendingOrderSettingsDefaults.bFixPipsMin,
        nFixPipsMin: initialData?.nFixPipsMin ?? PendingOrderSettingsDefaults.nFixPipsMin,
        bRateOfChangeMin: initialData?.bRateOfChangeMin ?? PendingOrderSettingsDefaults.bRateOfChangeMin,
        dRateOfChangeMin: initialData?.dRateOfChangeMin ?? PendingOrderSettingsDefaults.dRateOfChangeMin,
        bFixPipsMax: initialData?.bFixPipsMax ?? PendingOrderSettingsDefaults.bFixPipsMax,
        nFixPipsMax: initialData?.nFixPipsMax ?? PendingOrderSettingsDefaults.nFixPipsMax,
        bRateOfChangeMax: initialData?.bRateOfChangeMax ?? PendingOrderSettingsDefaults.bRateOfChangeMax,
        dRateOfChangeMax: initialData?.dRateOfChangeMax ?? PendingOrderSettingsDefaults.dRateOfChangeMax,
        nLocalPending: initialData?.nLocalPending ?? PendingOrderSettingsDefaults.nLocalPending,
        bHysteresisMin: initialData?.bHysteresisMin ?? PendingOrderSettingsDefaults.bHysteresisMin,
        nHysteresisMin: initialData?.nHysteresisMin ?? PendingOrderSettingsDefaults.nHysteresisMin,
        bHysteresisMax: initialData?.bHysteresisMax ?? PendingOrderSettingsDefaults.bHysteresisMax,
        nHysteresisMax: initialData?.nHysteresisMax ?? PendingOrderSettingsDefaults.nHysteresisMax,
      },
      enableReinitialize: true,
      validateOnChange: false,
      validateOnBlur: false,
      onSubmit: (values, { setSubmitting }) => {
        setTimeout(() => {
          try {
            onSave(values);
            setSuccess(true);
            setReturnMessage("Configuration saved successfully!");
            // autoCloseTimeoutRef.current = setTimeout(() => {
            //  handleClose();
            // }, 1500);
          } catch (err) {
            setError(true);
            setReturnMessage("Failed to save configuration.");
            console.error("Save error:", err);
          }
          setSubmitting(false);
        }, 100);
      },
    });

    const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
      formik;

    // ----------------------------------------------------------------------
    // Cross-validation logic
    // ----------------------------------------------------------------------
    useEffect(() => {
      const nFixPipsMin = parseFloat(values.nFixPipsMin);
      const nFixPipsMax = parseFloat(values.nFixPipsMax);
      const dRateOfChangeMin = parseFloat(values.dRateOfChangeMin);
      const dRateOfChangeMax = parseFloat(values.dRateOfChangeMax);
      const nHysteresisMin = parseFloat(values.nHysteresisMin);
      const nHysteresisMax = parseFloat(values.nHysteresisMax);

      // Check Fix Pips Min vs Max
      if (values.bFixPipsMin === true && values.bFixPipsMax === true && !Number.isNaN(nFixPipsMin) && !Number.isNaN(nFixPipsMax)) {
        if (nFixPipsMax <= nFixPipsMin) {
          setError(true);
          setReturnMessage(
            <>
              <b>Maximum pending price in fix pips</b> cannot be smaller or equal to <b>Minimum pending price in fix pips</b>!
            </>
          );
          return;
        }
      }

      // Check Rate of Change Min vs Max
      if (
        values.bRateOfChangeMin === true &&
        values.bRateOfChangeMax === true &&
        !Number.isNaN(dRateOfChangeMin) &&
        !Number.isNaN(dRateOfChangeMax)
      ) {
        if (dRateOfChangeMax <= dRateOfChangeMin) {
          setError(true);
          setReturnMessage(
            <>
              <b>Maximum pending price in rate of change</b> cannot be smaller or equal to <b>Minimum pending price in rate of change</b>!
            </>
          );
          return;
        }
      }

      // Hysteresis validation logic (converted from C++)
      const operation = nOperation;
      const nMode = values.nMode;

      // Validate hysteresis for operation "0" (off) and mode "2"
      if (operation === "0" && nMode === "2") {
        if (nHysteresisMin !== 0 || nHysteresisMax !== 0) {
          if (nHysteresisMin < nHysteresisMax) {
            if (nHysteresisMin > 0 && nHysteresisMax < 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
            if (nHysteresisMin < 0 && nHysteresisMax < 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
          } else if (nHysteresisMin > nHysteresisMax) {
            if (nHysteresisMin > 0 && nHysteresisMax > 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
            if (nHysteresisMin < 0 && nHysteresisMax > 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
          }
        }
      }

      // Validate hysteresis for operation "0" (off) and mode "1"
      if (operation === "0" && nMode === "1") {
        if (nHysteresisMin !== 0 || nHysteresisMax !== 0) {
          if (nHysteresisMin < nHysteresisMax) {
            if (nHysteresisMin > 0 && nHysteresisMax < 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
            if (nHysteresisMin < 0 && nHysteresisMax < 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
          } else if (nHysteresisMin > nHysteresisMax) {
            if (nHysteresisMin > 0 && nHysteresisMax > 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
            if (nHysteresisMin < 0 && nHysteresisMax > 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
          }
        }
      }

      // Validate hysteresis for operation "2" and mode "2"
      if (operation === "1" && nMode === "2") {
        if (nHysteresisMin !== 0 || nHysteresisMax !== 0) {
          if (nHysteresisMin < nHysteresisMax) {
            if (nHysteresisMin > 0 && nHysteresisMax < 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
            if (nHysteresisMin < 0 && nHysteresisMax < 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
          } else if (nHysteresisMin > nHysteresisMax) {
            if (nHysteresisMin > 0 && nHysteresisMax > 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
            if (nHysteresisMin < 0 && nHysteresisMax > 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
          }
        }
      }

      // Validate hysteresis for operation "2" and mode "1"
      if (operation === "1" && nMode === "1") {
        if (nHysteresisMin !== 0 || nHysteresisMax !== 0) {
          if (nHysteresisMin < nHysteresisMax) {
            if (nHysteresisMin > 0 && nHysteresisMax < 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
            if (nHysteresisMin < 0 && nHysteresisMax < 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
          } else if (nHysteresisMin > nHysteresisMax) {
            if (nHysteresisMin > 0 && nHysteresisMax > 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
            if (nHysteresisMin < 0 && nHysteresisMax > 0) {
              setError(true);
              setReturnMessage(
                <>
                  <b>Illogical hysteresis values</b> for hysteresis minimum and/or maximum!
                </>
              );
              return;
            }
          }
        }
      }

      // If all validations pass, clear error state
      setError(false);
      setReturnMessage("");
    }, [
      values.bFixPipsMin,
      values.nFixPipsMin,
      values.bFixPipsMax,
      values.nFixPipsMax,
      values.bRateOfChangeMin,
      values.dRateOfChangeMin,
      values.bRateOfChangeMax,
      values.dRateOfChangeMax,
      values.bHysteresisMin,
      values.nHysteresisMin,
      values.bHysteresisMax,
      values.nHysteresisMax,
      values.nMode,
      nOperation,
    ]);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleClickOpen = (scrollType) => () => {
      setOpen(true);
      setScroll(scrollType);
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    let saveAndCloseDisabled = false;
    if (values.nPrice === "2" && indicators && indicators.length === 0) {
      saveAndCloseDisabled = true;
    }
    if (
      values.nPrice === "2" &&
      (values.nInosUsedForValueInjection === "" || values.nInosUsedForValueInjection === "0" || values.nInosUsedForValueInjection === 0)
    ) {
      saveAndCloseDisabled = true;
    }
    if (values.nPrice === "3" && chartAnalyzers && chartAnalyzers.length === 0) {
      saveAndCloseDisabled = true;
    }
    if (
      values.nPrice === "3" &&
      (values.nCaSupResUsedForValueInjection === "" || values.nCaSupResUsedForValueInjection === "0" || values.nCaSupResUsedForValueInjection === 0)
    ) {
      saveAndCloseDisabled = true;
    }

    const hasValidationErrors = () => {
      return Object.keys(formik.errors).length > 0;
    };
    if (hasValidationErrors()) {
      saveAndCloseDisabled = true;
    }

    // Disable save button if cross-validation fails
    if (error) {
      saveAndCloseDisabled = true;
    }

    // ----------------------------------------------------------------------
    // Main component
    // ----------------------------------------------------------------------
    return (
      <>
        <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
          Configure pending order settings
        </B1Tiny>

        <ModalDialog
          open={open}
          handleClose={handleClose}
          scroll={scroll}
          outlet={
            <>
              <DialogTitle
                id="dialog-title"
                sx={{
                  background: "#F2F2F2",
                  textTransform: "none",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  alignContent: "center",
                  py: 1,
                  pl: 2,
                  pr: 1,
                }}
              >
                <Typography variant="contentBig">Configure pending order settings</Typography>

                <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                  <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
                </IconButton>
              </DialogTitle>

              <FormikProvider value={formik}>
                <DialogContent
                  dividers
                  sx={{
                    background: "#F2F2F2",
                    borderTop: "1px solid #C8C8C8",
                    borderBottom: "0px solid #C8C8C8",
                    px: 2,
                    pt: 2,
                    pb: 1,
                    width: "100%",
                    boxSizing: "border-box",
                    overflowY: "scroll",
                    overflowX: "hidden",
                    // Custom scrollbar styling
                    "&::-webkit-scrollbar": {
                      width: "16px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#e0e0e0",
                      borderRadius: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888888",
                      borderRadius: "6px",
                      border: "2px solid #e0e0e0",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#666666",
                    },
                    // Firefox scrollbar styling
                    scrollbarWidth: "thin",
                    scrollbarColor: "#888888 #e0e0e0",
                  }}
                >
                  <Stack spacing={2}>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nMode */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Mode"
                        helpReference="HandlePendingOrderSettings"
                        helpText=""
                        helpLink=""
                        fieldName="nMode"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select {...getFieldProps("nMode")} input={<ModalContentOutlinedInput />} error={Boolean(touched.nMode && errors.nMode)}>
                          <ModalContentMenuItem value="0">Off</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Stop</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">Limit</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {values.nMode !== "0" ? (
                      <>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nPrice */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Price calculation method"
                            helpReference="HandlePendingOrderSettings"
                            helpText=""
                            helpLink=""
                            fieldName="nPrice"
                            touched={touched}
                            errors={errors}
                          />
                          <FormControl fullWidth>
                            <Select
                              {...getFieldProps("nPrice")}
                              input={<ModalContentOutlinedInput />}
                              error={Boolean(touched.nPrice && errors.nPrice)}
                            >
                              <ModalContentMenuItem value="0">Fix pips</ModalContentMenuItem>
                              <ModalContentMenuItem value="1">Rate of change</ModalContentMenuItem>
                              {professionalMode === true || values.nPrice === "2" ? (
                                <ModalContentMenuItem value="2">Indicator value</ModalContentMenuItem>
                              ) : null}
                              {professionalMode === true || values.nPrice === "3" ? (
                                <ModalContentMenuItem value="3">Support/resistance from chart analyzer</ModalContentMenuItem>
                              ) : null}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nFixPipsActual */}
                        {/* ---------------------------------------------------------------------- */}
                        {values.nPrice === "0" ? (
                          <Box>
                            <SmartParameter
                              value="Fix pips"
                              helpReference="HandlePendingOrderSettings"
                              helpText=""
                              helpLink=""
                              fieldName="nFixPipsActual"
                              touched={touched}
                              errors={errors}
                            />
                            <SmartNumberInput textFieldName="nFixPipsActual" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                          </Box>
                        ) : null}

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dRateOfChangeActual */}
                        {/* ---------------------------------------------------------------------- */}
                        {values.nPrice === "1" ? (
                          <Box>
                            <SmartParameter
                              value="Rate of change"
                              helpReference="HandlePendingOrderSettings"
                              helpText=""
                              helpLink=""
                              fieldName="dRateOfChangeActual"
                              touched={touched}
                              errors={errors}
                            />
                            <SmartNumberInput textFieldName="dRateOfChangeActual" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                          </Box>
                        ) : null}

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nInosUsedForValueInjection */}
                        {/* ---------------------------------------------------------------------- */}
                        {values.nPrice === "2" &&
                        (() => {
                          const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                          return forwardIndicators.length > 0;
                        })() ? (
                          <Box>
                            <SmartParameter
                              value="Used indicator"
                              helpReference="HandlePendingOrderSettings"
                              helpText=""
                              helpLink=""
                              fieldName="nInosUsedForValueInjection"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nInosUsedForValueInjection")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nInosUsedForValueInjection && errors.nInosUsedForValueInjection)}
                              >
                                <ModalContentMenuItem value="0">
                                  <Box>
                                    <Typography variant="contentSmall">Please select ...</Typography>
                                  </Box>
                                </ModalContentMenuItem>
                                {indicators
                                  ?.filter((indicator) => indicator.config?.nSignalUsedFor === "2")
                                  .map((indicator) => (
                                    <ModalContentMenuItem key={indicator.id.toString()} value={indicator.id.toString()}>
                                      <Box>
                                        <Typography variant="contentSmall">
                                          {indicator.name} ({indicator.subtitle})
                                        </Typography>
                                      </Box>
                                    </ModalContentMenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ) : null}

                        {values.nPrice === "2" &&
                        (() => {
                          const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                          return forwardIndicators.length === 0;
                        })() ? (
                          <Box sx={{ m: 0, p: 0 }}>
                            <ModalAlertError
                              sx={{
                                m: "0 !important",
                              }}
                              severity="error"
                            >
                              No indicator with <b>Signal used for = Forward</b> is available. Please add at least one indicator and set its{" "}
                              <b>Signal used for</b> setting to <b>Forward</b>.
                            </ModalAlertError>
                          </Box>
                        ) : null}

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nCaSupResUsedForValueInjection */}
                        {/* ---------------------------------------------------------------------- */}
                        {values.nPrice === "3" &&
                        (() => {
                          const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
                          return forwardChartAnalyzers.length > 0;
                        })() ? (
                          <Box>
                            <SmartParameter
                              value="Used chart analyzer"
                              helpReference="HandlePendingOrderSettings"
                              helpText=""
                              helpLink=""
                              fieldName="nCaSupResUsedForValueInjection"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nCaSupResUsedForValueInjection")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nCaSupResUsedForValueInjection && errors.nCaSupResUsedForValueInjection)}
                              >
                                <ModalContentMenuItem value="0">
                                  <Box>
                                    <Typography variant="contentSmall">Please select ...</Typography>
                                  </Box>
                                </ModalContentMenuItem>
                                {chartAnalyzers
                                  ?.filter((chartAnalyzer) => chartAnalyzer.config?.nSignalUsedFor === "2")
                                  .map((chartAnalyzer) => (
                                    <ModalContentMenuItem key={chartAnalyzer.id} value={chartAnalyzer.id.toString()}>
                                      <Box>
                                        <Typography variant="contentSmall">
                                          {chartAnalyzer.name} ({chartAnalyzer.subtitle})
                                        </Typography>
                                      </Box>
                                    </ModalContentMenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ) : null}

                        {values.nPrice === "3" &&
                        (() => {
                          const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
                          return forwardChartAnalyzers.length === 0;
                        })() ? (
                          <Box sx={{ m: 0, p: 0 }}>
                            <ModalAlertError
                              sx={{
                                m: "0 !important",
                              }}
                              severity="error"
                            >
                              No chart analyzer with <b>Signal used for = Forward</b> is available. Please add at least one chart analyzer and set its{" "}
                              <b>Signal used for</b> setting to <b>Forward</b>.
                            </ModalAlertError>
                          </Box>
                        ) : null}

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nLimits */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Limits behaviour"
                            helpReference="HandlePendingOrderSettings"
                            helpText=""
                            helpLink=""
                            fieldName="nLimits"
                            touched={touched}
                            errors={errors}
                          />
                          <FormControl fullWidth>
                            <Select
                              {...getFieldProps("nLimits")}
                              input={<ModalContentOutlinedInput />}
                              error={Boolean(touched.nLimits && errors.nLimits)}
                            >
                              <ModalContentMenuItem value="0">Initial</ModalContentMenuItem>
                              <ModalContentMenuItem value="1">TP moved</ModalContentMenuItem>
                              <ModalContentMenuItem value="2">SL moved</ModalContentMenuItem>
                              <ModalContentMenuItem value="3">SL and TP moved</ModalContentMenuItem>
                              <ModalContentMenuItem value="4">TP from chart analyzer</ModalContentMenuItem>
                              <ModalContentMenuItem value="5">SL from chart analyzer</ModalContentMenuItem>
                              <ModalContentMenuItem value="6">SL and TP from chart analyzer</ModalContentMenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nReference */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Used price reference"
                            helpReference="HandlePendingOrderSettings"
                            helpText=""
                            helpLink=""
                            fieldName="nReference"
                            touched={touched}
                            errors={errors}
                          />
                          <FormControl fullWidth>
                            <Select
                              {...getFieldProps("nReference")}
                              input={<ModalContentOutlinedInput />}
                              error={Boolean(touched.nPrice && errors.nPrice)}
                            >
                              <ModalContentMenuItem value="0">Aks / bid</ModalContentMenuItem>
                              <ModalContentMenuItem value="1">Ask / bid center</ModalContentMenuItem>
                              <ModalContentMenuItem value="2">Median price</ModalContentMenuItem>
                              <ModalContentMenuItem value="3">Typical price</ModalContentMenuItem>
                              <ModalContentMenuItem value="4">Weighted price</ModalContentMenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nExpiration */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Expiration"
                            helpReference="HandlePendingOrderSettings"
                            helpText=""
                            helpLink=""
                            fieldName="nExpiration"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="nExpiration" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Divider */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Grid */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nFixPipsMin */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Minimum pending price in fix pips"
                              helpReference="HandlePendingOrderSettings"
                              helpText=""
                              helpLink=""
                              fieldName="nFixPipsMin"
                              touched={touched}
                              errors={errors}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <ModalContentSwitch
                                sx={{ my: "12px" }}
                                checked={values.bFixPipsMin === true}
                                onChange={(event) => {
                                  setFieldValue("bFixPipsMin", event.target.checked);
                                }}
                              />
                              {values.bFixPipsMin === true ? (
                                <SmartNumberInput textFieldName="nFixPipsMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                              ) : null}
                            </Box>
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* dRateOfChangeMin */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Minimum value in rate of change"
                              helpReference="HandlePendingOrderSettings"
                              helpText=""
                              helpLink=""
                              fieldName="dRateOfChangeMin"
                              touched={touched}
                              errors={errors}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <ModalContentSwitch
                                sx={{ my: "12px" }}
                                checked={values.bRateOfChangeMin === true}
                                onChange={(event) => {
                                  setFieldValue("bRateOfChangeMin", event.target.checked);
                                }}
                              />
                              {values.bRateOfChangeMin === true ? (
                                <SmartNumberInput textFieldName="dRateOfChangeMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                              ) : null}
                            </Box>
                          </Box>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Divider */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Grid */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nFixPipsMax */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Maximum pending price in fix pips"
                              helpReference="HandlePendingOrderSettings"
                              helpText=""
                              helpLink=""
                              fieldName="nFixPipsMax"
                              touched={touched}
                              errors={errors}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <ModalContentSwitch
                                sx={{ my: "12px" }}
                                checked={values.bFixPipsMax === true}
                                onChange={(event) => {
                                  setFieldValue("bFixPipsMax", event.target.checked);
                                }}
                              />
                              {values.bFixPipsMax === true ? (
                                <SmartNumberInput textFieldName="nFixPipsMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                              ) : null}
                            </Box>
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* dRateOfChangeMax */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Maximum pending price in rate of change"
                              helpReference="HandlePendingOrderSettings"
                              helpText=""
                              helpLink=""
                              fieldName="dRateOfChangeMax"
                              touched={touched}
                              errors={errors}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <ModalContentSwitch
                                sx={{ my: "12px" }}
                                checked={values.bRateOfChangeMax === true}
                                onChange={(event) => {
                                  setFieldValue("bRateOfChangeMax", event.target.checked);
                                }}
                              />
                              {values.bRateOfChangeMax === true ? (
                                <SmartNumberInput textFieldName="dRateOfChangeMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                              ) : null}
                            </Box>
                          </Box>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Divider */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nLocalPending */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Locally managed limit and stop orders"
                            helpReference="HandlePendingOrderSettings"
                            helpText=""
                            helpLink=""
                            fieldName="nLocalPending"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nLocalPending === true}
                            onChange={(event) => {
                              setFieldValue("nLocalPending", event.target.checked);
                            }}
                          />
                        </Box>

                        {values.nLocalPending === true ? (
                          <>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Divider */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Headline */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <Typography variant="h3" sx={{ mt: 0.5 }}>
                                Price Hysteresis
                              </Typography>
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Help Sections */}
                            {/* ---------------------------------------------------------------------- */}
                            {PENDING_ORDER_SETTINGS_HYSTERESIS_HELP_IMAGE && (
                              <Box>
                                <B3Tiny
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => {
                                    setCollapseHysteresis(!collapseHysteresis);
                                  }}
                                >
                                  {collapseHysteresis ? "Hide help section" : "Show help section"}
                                </B3Tiny>

                                <Collapse sx={{ p: "3px" }} in={collapseHysteresis} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1 }}>
                                    <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                                      {PENDING_ORDER_SETTINGS_HYSTERESIS_HELP_DESCRIPTION}
                                      <br />
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      borderRadius: "10px",
                                      py: 2,
                                      boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    <CardMedia
                                      component="img"
                                      image={PENDING_ORDER_SETTINGS_HYSTERESIS_HELP_IMAGE}
                                      alt="Pending order with hysteresis"
                                      sx={{
                                        objectFit: "contain",
                                        maxHeight: "834px",
                                      }}
                                    />
                                  </Box>
                                </Collapse>
                              </Box>
                            )}
                          </>
                        ) : null}

                        {/* ---------------------------------------------------------------------- */}
                        {/* Grid */}
                        {/* ---------------------------------------------------------------------- */}
                        {values.nLocalPending === true ? (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 2,
                            }}
                          >
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nHysteresisMin */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Hysteresis minimum value"
                                helpReference="HandlePendingOrderSettings"
                                helpText=""
                                helpLink=""
                                fieldName="nHysteresisMin"
                                touched={touched}
                                errors={errors}
                              />
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <ModalContentSwitch
                                  sx={{ my: "12px" }}
                                  checked={values.bHysteresisMin === true}
                                  onChange={(event) => {
                                    setFieldValue("bHysteresisMin", event.target.checked);
                                  }}
                                />
                                {values.bHysteresisMin === true ? (
                                  <SmartNumberInput textFieldName="nHysteresisMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                ) : null}
                              </Box>
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nHysteresisMax */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Hysteresis maximum value"
                                helpReference="HandlePendingOrderSettings"
                                helpText=""
                                helpLink=""
                                fieldName="nHysteresisMax"
                                touched={touched}
                                errors={errors}
                              />
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <ModalContentSwitch
                                  sx={{ my: "12px" }}
                                  checked={values.bHysteresisMax === true}
                                  onChange={(event) => {
                                    setFieldValue("bHysteresisMax", event.target.checked);
                                  }}
                                />
                                {values.bHysteresisMax === true ? (
                                  <SmartNumberInput textFieldName="nHysteresisMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                ) : null}
                              </Box>
                            </Box>
                          </Box>
                        ) : null}
                      </>
                    ) : null}
                  </Stack>
                </DialogContent>

                <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                  <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
                </Box>

                <DialogActions
                  sx={{
                    background: "#F2F2F2",
                    p: 2,
                    pt: 0,
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                    <ModalActionsLoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleReset}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Reset
                    </ModalActionsLoadingButton>

                    <ModalActionsLoadingButton
                      fullWidth
                      disabled={saveAndCloseDisabled}
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Save
                    </ModalActionsLoadingButton>

                    <ModalActionsLoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleClose}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Close
                    </ModalActionsLoadingButton>
                  </Box>
                </DialogActions>
              </FormikProvider>
            </>
          }
        />
      </>
    );
  }
);
